import { type MutableRefObject, type RefObject, useCallback, useMemo, useRef } from 'react';
import { type Metric } from 'src/home/reportTemplateParameters';
import { getScorecardMetricElementId } from 'src/home/copilot/scorecardElements';
import { type CopilotPageElementRefsById } from 'shared/context/CopilotContext';

/**
 * Creates a ref object that reads the latest registered DOM node for a scorecard metric.
 * Backed by a mutable map updated when individual scorecard cards mount and unmount.
 */
const createScorecardMetricElementRef = (elementsByMetricRef: MutableRefObject<Map<string, HTMLDivElement | null>>, metricId: Metric): RefObject<HTMLDivElement | null> => ({
  get current() {
    return elementsByMetricRef.current.get(metricId) ?? null;
  }
});

/**
 * Manages per-metric scorecard element refs for copilot highlight and inspect tools.
 *
 * Registers DOM nodes as scorecard cards mount, and rebuilds the element-ref map
 * whenever the visible scorecard metric selection changes.
 */
export const useHomeCopilotScorecardElementRefs = (selectedMetrics: Metric[]): {
  registerScorecardElement: (metricId: Metric, element: HTMLDivElement | null) => void;
  scorecardMetricElementRefsById: CopilotPageElementRefsById;
} => {
  const elementsByMetricRef = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const registerScorecardElement = useCallback((metricId: Metric, element: HTMLDivElement | null) => {
    if (element == null) {
      elementsByMetricRef.current.delete(metricId);
      return;
    }
    elementsByMetricRef.current.set(metricId, element);
  }, []);
  const scorecardMetricElementRefsById = useMemo(() => Object.fromEntries(selectedMetrics.map(metricId_0 => [getScorecardMetricElementId(metricId_0), [createScorecardMetricElementRef(elementsByMetricRef, metricId_0)]])), [selectedMetrics]);
  return {
    registerScorecardElement,
    scorecardMetricElementRefsById
  };
};